// Copyright 2024 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable, observable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { LineData, PlotData } from "controls/plot/graph-plot/GraphPlot";
import { LegendGroup } from "controls/plot/parts/LegendPart";
import { Toast } from "controls/toast/Toast";
import { ArrayToMovingAverageArrayConverter } from "converters/ArrayToMovingAverageArrayConverter";
import { UnitValueToStringValueConverter } from "converters/UnitValueToStringValueConverter";
import { MeasurementChannelSelection } from "datatypes/MeasurementChannelSelection";
import { Point2D, Point3D, Range } from "datatypes/SoramaLocalApi";
import { GenericMeasurement, MeasurementPartType } from "datatypes/SoramaWebApi";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { Point3DExtension } from "extensions/Point3DExtension";
import { IExportable } from "libraries/Export/IExportable";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleDeactivate,
    IWorkflowModuleExportVisual,
    IWorkflowModuleInstance,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ImageExportState, LogoPositions } from "modules/modals/ImageExport/ImageExportModal";
import {
    ManualChannelSelectionModalReturnState,
    ManualChannelSelectionModalState,
} from "modules/modals/ManualChannelSelection/ManualChannelSelectionModal";
import { SettingsModalState } from "modules/modals/SettingsModal";
import {
    AnnotatedTimeData,
    TimeDataSet,
    TimeSelectionModalState,
} from "modules/modals/TimeSelection/TimeSelectionModal";
import { MeasurementField, MeasurementRepository } from "repositories/MeasurementRepository";
import { MeasurementColorService } from "services/Color/MeasurementColorService";
import { SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IPlayablePropertiesService } from "services/PlayableProperties/IPlayablePropertiesService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import Sortable from "sortablejs";
import { Pressure } from "units/Pressure";
import { Time } from "units/Time";
import { Unit } from "units/Unit";
import { v4 as uuidv4 } from "uuid";
import {
    AllPsychoAcousticMetricTypes,
    PsychoAcousticMetricType,
    PsychoAcousticUnits,
} from "../PsychoAcoustics/SpiderChart/SpiderChart";
import { AudioSignalProvider } from "./Providers/AudioSignalProvider";
import { ITimeSignalProvider } from "./Providers/ITimeSignalProvider";
import { PsychoAcousticSignalProvider } from "./Providers/PsychoAcousticSignalProvider";

export interface TimeDataSetWithAudio extends TimeDataSet {
    /** If not specified, anything trying to use audioData should use timeDatas property of TimeDataSet */
    audioDatas?: AnnotatedTimeData[];
}

@transient()
@autoinject
export class TimeModule implements IWorkflowModuleActivate, IWorkflowModuleDeactivate, IWorkflowModuleExportVisual {
    private dataExportable: IExportable;
    public static UnitValueToStringValueConverter = new UnitValueToStringValueConverter();
    private moduleInstance: IWorkflowModuleInstance;
    private timeRangeModal: IWorkflowModuleModalInstance<TimeSelectionModalState, void>;
    private timeSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<TimeModule>, void>;
    private plotTimeSignals: PlotData[];

    /** The actual time signal for the plot */
    private timeSignalProvider: ITimeSignalProvider;
    /** In case the time signal is not audio, we use this to get the data for the audio player */
    private audioTimeSignalProvider: ITimeSignalProvider;

    // Module properties
    @computedFrom("busy", "selectedMeasurementIds")
    public get disableExport(): boolean {
        return this.busy || this.selectedMeasurementIds.length == 0;
    }

    // User Settings Storage Keys
    private static instanceCount = 0;

    protected localPlayableId: string;
    protected globalPlayableId: string = IPlayablePropertiesService.GlobalTimePlayablePropertyId;
    protected playableId = IPlayablePropertiesService.GlobalTimePlayablePropertyId;

    // View Fields
    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        scope: WorkflowPropertyScope.workflow,
        defaultValue: false,
    })
    public showLegend: boolean;

    public legendArray: LegendGroup[] = [];

    public zoomModes: { name: string; value: string }[] = [
        { name: "X Axis", value: "x" },
        { name: "Y Axis", value: "y" },
        { name: "XY Axis", value: "xy" },
    ];

    public zoomMode = "x";

    public signalUnit: Unit = Pressure.pascal;
    public signalUnitRMS: Unit = Pressure.pascalRms;
    public displayTimeUnit: Unit = Time.second;
    public displayTimeSelectionUnit: Unit = Time.milliSecond;

    //#region State Fields

    // Flag that indicates whether the module is busy
    protected busy: boolean;

    /**
     * Contains a "cached" list of time signals
     */
    private currentTimeSignals: TimeDataSetWithAudio[] = [];
    public measurementChannelsPair: AnnotatedTimeData[] = [];

    private colorObserverDisposables: Disposable[] = [];
    private measurementUpdateDisposables: Disposable[] = [];

    public progresses: { [key: string]: number } = {};

    @computedFrom("progresses")
    protected get progressPercentage(): number {
        if (this.progresses == undefined || Object.keys(this.progresses).length == 0) return 0;
        let totalProgress = 0;
        for (let key in this.progresses) {
            totalProgress += this.progresses[key];
        }
        let avgProgress = totalProgress / Object.keys(this.progresses).length;
        return avgProgress * 95; // Use 95 so we never wait at 100%
    }

    // Id used to target this specific module in the DOM (for sortablejs)
    private uniqueId: string = uuidv4();

    // Preserves the cancelation state of the previous updateData call
    private updateDataAbortController: AbortController;

    // Flag that indicates whether the module has received any data
    protected hasData: boolean;

    // Preserves the type of the psychoacoustic metric type
    protected metricType?: PsychoAcousticMetricType;

    //#endregion

    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflowModule })
    public isPsychoacousticAnalysis: boolean;

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "string",
        changedHandler: "selectedMeasurementChanged",
    })
    public selectedMeasurementIds: string[] = [];

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "string",
        changedHandler: "selectedMeasurementChanged",
    })
    public selectedPsychoAcousticMeasurementIds: string[] = [];

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        scope: WorkflowPropertyScope.workflowModule,
        objectType: "MeasurementChannelSelection",
        changedHandler: "selectedMeasurementChanged",
    })
    public selectedChannelsForMeasurementIds: MeasurementChannelSelection[] = [];

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "Point3D",
        changedHandler: "selectedMeasurementChanged",
    })
    public selectedMic: Point3D;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public isTimeRangeLinked: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public isSignalRangeLinked: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public timeRange: Range;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public signalRange: Range;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "range" })
    public timeSelectionRanges: SelectableRange[] = [];

    //#endregion

    //#region Settings

    @observable({ changeHandler: "timeRangeInputChanged" })
    public minXAxis = 0;

    @observable({ changeHandler: "timeRangeInputChanged" })
    public maxXAxis = 1;

    @observable({ changeHandler: "signalRangeInputChanged" })
    @userSetting()
    public minYAxis = -20;

    @observable({ changeHandler: "signalRangeInputChanged" })
    @userSetting()
    public maxYAxis = 20; // 120 dB = 20 Pa

    public minTime = 0;
    public maxTime = 1;

    public minSignal = -20;
    public maxSignal = 20;

    @observable
    public showRms = false;
    //#endregion

    //#region Constructor and Initialization
    constructor(
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementColorService: MeasurementColorService,
        private readonly bindingEngine: BindingEngine,
        private readonly playablePropertiesService: IPlayablePropertiesService,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    public attached() {
        const draggableList = document.getElementById(`time-draggable-list-${this.uniqueId}`);
        Sortable.create(draggableList, {
            handle: ".grip-icon",
            animation: 150, // Animation duration in ms
            onEnd: (event) => {
                const oldIndex = event.oldIndex;
                const newIndex = event.newIndex;

                // Reorder the `measurementChannelsPair` array
                const movedItem = this.measurementChannelsPair.splice(oldIndex, 1)[0];
                this.measurementChannelsPair.splice(newIndex, 0, movedItem);

                // Update the plot + legend
                this.updatePlotTimeSignals();
                this.updateLegendArray();
            },
        });

        // Restore to the original state
        this.minSignal = this.minYAxis;
        this.maxSignal = this.maxYAxis;
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        if (this.moduleInstance.variant) {
            if (AllPsychoAcousticMetricTypes.includes(this.moduleInstance.variant as PsychoAcousticMetricType)) {
                this.metricType = this.moduleInstance.variant as PsychoAcousticMetricType;
                this.isPsychoacousticAnalysis = true; // Set the flag to true
            } else {
                Toast.error(
                    `Invalid variant for TimeModule: ${this.moduleInstance.variant}.
                     It is not a PsychoAcoustic Metric Type.`
                );
            }
        }

        this.audioTimeSignalProvider = new AudioSignalProvider(this.processorResolver);

        if (this.isPsychoacousticAnalysis) {
            this.moduleInstance.title = `${this.metricType} - Time`;
            this.timeSignalProvider = new PsychoAcousticSignalProvider(this.processorResolver, this.metricType);
            this.signalUnit = PsychoAcousticUnits[this.metricType];
        } else {
            this.timeSignalProvider = this.audioTimeSignalProvider;
        }

        this.localPlayableId = TimeModule.name + (TimeModule.instanceCount++).toString();

        this.selectedMeasurementChanged();
    }

    public deactivateModule(): void {
        // Clear playback service
        if (this.playablePropertiesService.playableProperties[this.localPlayableId]) {
            this.playablePropertiesService.unregister(this.localPlayableId);
        }

        // Clear disposables
        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop()?.dispose();
        }

        // Clear disposables
        while (this.colorObserverDisposables.length > 0) {
            this.colorObserverDisposables.pop()?.dispose();
        }

        // Clear locally stored data
        if (this.currentTimeSignals) {
            this.currentTimeSignals.splice(0, this.currentTimeSignals.length);
        }

        if (this.plotTimeSignals) {
            this.plotTimeSignals.splice(0, this.plotTimeSignals.length);
        }
    }

    //#endregion

    //#region Change callbacks

    public timeRangeChanged(newRange: Range): void {
        if (this.isTimeRangeLinked && newRange) {
            this.minXAxis = newRange.from;
            this.maxXAxis = newRange.to;
        }
    }

    private signalRangeChanged(newRange: Range): void {
        if (this.isSignalRangeLinked && newRange) {
            this.minYAxis = newRange.from;
            this.maxYAxis = newRange.to;

            // Set zooming boundaries for the signal range
            this.minSignal = this.minYAxis;
            this.maxSignal = this.maxYAxis;
        }
    }

    private async selectedMeasurementChanged(): Promise<void> {
        // If this is a psychoAcoustic time analysis module, get the psychoAcoustic measurement ids instead
        if (
            this.isPsychoacousticAnalysis &&
            this.selectedMeasurementIds !== this.selectedPsychoAcousticMeasurementIds
        ) {
            this.selectedMeasurementIds = this.selectedPsychoAcousticMeasurementIds;
        }

        if (this.moduleInstance) {
            this.moduleInstance.cancelModal();
        }

        // Clear measurement update observers
        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }

        // Add new observers
        for (const measurementId of this.selectedMeasurementIds) {
            this.measurementUpdateDisposables.push(
                this.measurementRepository.measurementUpdateObserver(this.measurementUpdated, measurementId)
            );
        }

        // Keep the busy state until all the promises are resolved
        this.busy = true;

        await this.syncMicrophoneSelection();
        await this.syncCurrentTimeSignals();
        await this.updateData();

        // Data has been updated
        this.busy = false;
    }

    /**
     * Synchronize the psychoacousticsselected microphone with the channel selection.
     */
    private async syncMicrophoneSelection(): Promise<void> {
        // If this is psychoacoustics time and we have a selected mic, make sure the channelSelection reflects this
        if (this.isPsychoacousticAnalysis && this.selectedMic) {
            const newSelectedChannelsForMeasurementIds: MeasurementChannelSelection[] = [];
            // For each measurement, get their mic positions
            for (const measurementId of this.selectedMeasurementIds) {
                let measurement = this.currentTimeSignals.find((x) => x.measurement.id === measurementId)?.measurement;
                if (!measurement) {
                    measurement = await this.measurementRepository.getMeasurement(measurementId);
                }

                const micPositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(measurement);
                const selectedMicIndex = micPositions.findIndex((micPos) =>
                    Point3DExtension.Equals(micPos, this.selectedMic)
                );

                newSelectedChannelsForMeasurementIds.push({
                    measurementId: measurementId,
                    selectedChannels: [selectedMicIndex],
                });
            }

            if (
                !ObjectExtensions.deepEqual(
                    newSelectedChannelsForMeasurementIds,
                    this.selectedChannelsForMeasurementIds
                )
            ) {
                this.selectedChannelsForMeasurementIds.splice(
                    0,
                    this.selectedChannelsForMeasurementIds.length,
                    ...newSelectedChannelsForMeasurementIds
                );
            }
        }
    }

    private async syncCurrentTimeSignals(): Promise<void> {
        if (!this.moduleInstance) return;

        return this.moduleInstance.dispatch(async () => {
            const promises: Promise<void>[] = [];

            // update "cached" list of time signals
            this.currentTimeSignals = this.getSelectedTimeSignals();

            // Load new measurements
            for (const measurementId of this.selectedMeasurementIds) {
                let timeDataSet = this.currentTimeSignals.find((x) => x.measurement.id == measurementId);

                if (!timeDataSet) {
                    promises.push(this.addMeasurementToTimeSignals(measurementId));
                } else {
                    timeDataSet = this.verifyChannelSelection(timeDataSet, measurementId);
                }
            }

            return Promise.all(promises).then(() => {});
        });
    }

    /**
     * Filters the current time signals based on the selected measurement IDs.
     *
     * @returns The filtered time signals.
     */
    private getSelectedTimeSignals(): TimeDataSetWithAudio[] {
        let currentTs = this.currentTimeSignals;
        const selected: TimeDataSetWithAudio[] = [];

        for (const key of this.selectedMeasurementIds) {
            let found = false;
            currentTs = currentTs.filter((item) => {
                if (!found && item.measurement.id == key) {
                    selected.push(item);
                    found = true;
                    return false;
                } else return true;
            });
        }

        return selected;
    }

    private async addMeasurementToTimeSignals(measurementId: string): Promise<void> {
        const genericMeasurement: GenericMeasurement = await this.measurementRepository.getMeasurement(measurementId);
        const numberOfChannels = MeasurementExtensions.getNumberOfChannels(genericMeasurement);

        const selectedChannels: boolean[] = Array<boolean>(numberOfChannels).fill(false);
        genericMeasurement.parts.map((p) => p.position);

        const channels = this.selectedChannelsForMeasurementIds.filter((x) => x.measurementId === measurementId).pop();

        if (!channels) {
            selectedChannels[0] = true;

            // Add a default selected channel if non exist
            this.selectedChannelsForMeasurementIds.push({
                measurementId: measurementId,
                selectedChannels: selectedChannels.map((x, idx) => (x ? idx : undefined)).filter((x) => x != undefined),
            });
        } else {
            for (const idx of channels.selectedChannels) {
                selectedChannels[idx] = true;
            }
        }

        const timeDataSet = <TimeDataSetWithAudio>{
            measurement: genericMeasurement,
            selectedChannels: selectedChannels,
            timeDatas: Array<AnnotatedTimeData>(numberOfChannels).fill(undefined),
            audioDatas: Array<AnnotatedTimeData>(numberOfChannels).fill(undefined),
        };

        this.currentTimeSignals.push(timeDataSet);
    }

    /**
     * Check if channels is still correct
     * This function directly modifies the `selectedChannels` property of the passed `timeDataSet` object.
     *
     * @param timeDataSet The TimeDataSet to verify and update.
     * @param measurementId The measurement ID to use for verifying the channel selection.
     * @returns The modified TimeDataSet object.
     */
    private verifyChannelSelection(timeDataSet: TimeDataSetWithAudio, measurementId: string): TimeDataSetWithAudio {
        const channels = this.selectedChannelsForMeasurementIds.find((x) => x.measurementId === measurementId);
        const selectedChannels: boolean[] = Array<boolean>(timeDataSet.selectedChannels.length).fill(false);
        if (channels) {
            for (const idx of channels.selectedChannels) {
                selectedChannels[idx] = true;
            }
        }

        timeDataSet.selectedChannels = selectedChannels;
        return timeDataSet;
    }

    public timeRangeInputChanged(): void {
        this.timeRange = <Range>{
            from: this.minXAxis,
            to: this.maxXAxis,
        };
    }

    private signalRangeInputChanged(): void {
        this.signalRange = <Range>{
            from: this.minYAxis,
            to: this.maxYAxis,
        };
    }

    private isTimeRangeLinkedChanged(): void {
        // Trigger update
        this.timeRangeChanged(this.timeRange);
    }

    private isSignalRangeLinkedChanged(): void {
        // Trigger update
        this.signalRangeChanged(this.signalRange);
    }

    private measurementUpdated = async (property: MeasurementField, measurement: GenericMeasurement): Promise<void> => {
        // Check if we need to invalidate some data
        if (property === MeasurementField.mutedSensors) {
            // Only computed measurements will be updated
            const findFunction = () =>
                this.currentTimeSignals.findIndex(
                    (x) =>
                        x.measurement.isComputedMeasurement &&
                        MeasurementExtensions.isRelatedTo(x.measurement, measurement.id)
                );
            let invalidIndex = findFunction();
            while (invalidIndex !== -1) {
                this.currentTimeSignals.splice(invalidIndex, 1);
                invalidIndex = findFunction();
            }

            await this.updateData();
        }
    };

    public showRmsChanged(): void {
        void this.updateData();
    }

    protected async selectedChannelsForMeasurementIdsChanged(): Promise<void> {
        await this.syncMicrophoneSelection();
        await this.syncCurrentTimeSignals();
        await this.updateData();
    }

    //#endregion

    //#region Methods

    private async updateData(): Promise<void> {
        if (this.moduleInstance == undefined) return;

        // Keep the busy state until all the promises are resolved
        this.busy = true;

        // Flag that indicates whether the module has received any data
        this.hasData = true;

        // Cancel any ongoing updateData request
        if (this.updateDataAbortController) {
            this.updateDataAbortController.abort();
        }
        // Create a new controller for this request
        this.updateDataAbortController = new AbortController();

        const abortSignal = this.updateDataAbortController.signal;

        await this.moduleInstance.dispatch(async () => {
            if (!this.selectedMeasurementIds?.length || !this.currentTimeSignals?.length) return;

            this.progresses = {};
            this.busy = true;
            const promises: Promise<void>[] = [];
            let nrOfParts = this.currentTimeSignals
                .map((x) => {
                    return (
                        x.selectedChannels.filter((y) => y).length - x.timeDatas.filter((y) => y != undefined).length
                    );
                })
                .reduce((total, x) => total + x);

            if (this.isPsychoacousticAnalysis) {
                // If psychoacoustic analysis, we need to get both the metric over time and the audio, so twice the signals
                nrOfParts *= 2;
            }

            if (abortSignal.aborted) return;

            for (let i = 0; i < this.currentTimeSignals.length; i++) {
                const signal = this.currentTimeSignals[i];

                // Check if the request was aborted
                if (abortSignal.aborted) return;
                const micPositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(signal.measurement);

                // Get only the indexes of the selected channels
                const selectedChannelIndexes = signal.selectedChannels
                    .map((isSelected, index) => (isSelected ? index : null))
                    .filter((index) => index !== null);

                for (const j of selectedChannelIndexes) {
                    if (signal.timeDatas[j] == undefined) {
                        // Check if the request was aborted
                        if (abortSignal.aborted) return;

                        promises.push(
                            this.getTimeData(signal.measurement, micPositions[j]).then((timeData) => {
                                if (timeData) {
                                    signal.timeDatas.splice(j, 1, timeData);
                                    signal.selectedChannels.splice(j, 1, true);
                                }
                            })
                        );

                        if (this.isPsychoacousticAnalysis) {
                            const audioPromise = async () => {
                                if (abortSignal.aborted) return;
                                const audioData = await this.getAudioData(signal.measurement, micPositions[j]);

                                signal.audioDatas.splice(j, 1, audioData);
                            };
                            promises.push(audioPromise());
                        }
                    }
                }
            }

            try {
                // Check if the request was aborted
                if (abortSignal.aborted) return;

                await Promise.all(promises);
                this.measurementChannelsPair = this.getAnnotatedTimeData();
                this.updatePlotTimeSignals();
                this.updateLegendArray();

                // Generate a timeline event
                void this.moduleInstance.createTimelineEvent();
            } catch (error) {
                console.error("Error updating data:", error);
                throw error;
            } finally {
                // Keep the busy state until all the promises are resolved
                this.busy = false;
            }
        });
    }

    /**
     * Get the AnnotatedTimeData objects from the selected measurement+mic combo.
     * Assumes that the currentTimeSignals have been updated with the needed data.
     * This update is done in the updateData method.
     */
    private getAnnotatedTimeData(): AnnotatedTimeData[] {
        const result: AnnotatedTimeData[] = [];
        for (let i = 0; i < this.currentTimeSignals.length; i++) {
            const signal = this.currentTimeSignals[i];
            for (let j = 0; j < signal.selectedChannels.length; j++) {
                if (signal.selectedChannels[j]) {
                    let annotatedTimeData = signal.timeDatas[j];
                    if (!annotatedTimeData) continue;
                    annotatedTimeData.micId = j;

                    if (this.isPsychoacousticAnalysis) {
                        const audioData = signal.audioDatas?.[j];
                        if (!audioData) continue;

                        annotatedTimeData.audioDataPoints = audioData.dataPoints;
                        annotatedTimeData.audioMinVal = audioData.minVal;
                        annotatedTimeData.audioMaxVal = audioData.maxVal;
                    }
                    result.push(annotatedTimeData);
                }
            }
        }
        return result;
    }

    private async getTimeData(measurement: GenericMeasurement, mic: Point3D): Promise<AnnotatedTimeData> {
        let signal: Point2D[] = undefined;
        try {
            signal = await this.timeSignalProvider.getTimeSignal(measurement, mic, (progress) => {
                this.updateProgress(measurement, mic, "time", progress);
            });
        } catch (error) {
            Toast.error(error?.message, "Please select a different measurement", this.uniqueId);
            return undefined;
        }

        // Filter out unwanted values from the signal
        let dataPoints = signal.filter(
            (point) => Number.isFinite(point.y) && !Number.isNaN(point.y) && point.y !== undefined
        );

        // Handle the case if we have no time data
        if (dataPoints.every((point) => point.y == 0 || point.x == 0)) {
            Toast.error(
                `No time data received for ${measurement.name}`,
                "Please select a different measurement.",
                this.uniqueId
            );
            this.hasData = false;
            throw new Error(`No time data received for ${measurement.name}`);
        }

        const minmax = ArrayExtensions.minmax(dataPoints.map((point) => point.y));
        const subIdx = MeasurementExtensions.micToSubIndexString(measurement, mic);

        // Ensure that the subscription uses a consistent identifier across all workflow modules
        const id = { measurementId: measurement.id };
        this.measurementColorService.ensureAssignedColor(id);

        // Observe color changes
        this.colorObserverDisposables.push(
            this.measurementColorService.subscribe(id, (color) => {
                // update the color picker by doing a deep copy
                this.currentTimeSignals.forEach((x: TimeDataSet) => {
                    x.measurement = <GenericMeasurement>ObjectExtensions.copy(x.measurement);
                });
                this.updateData();
                this.updatePlotTimeSignals();
            })
        );

        return <AnnotatedTimeData>{
            measurement: measurement,
            dataPoints: dataPoints,
            name: MeasurementExtensions.getDisplaySubIndexDisplayName(measurement, subIdx),
            minVal: minmax.min,
            maxVal: minmax.max,
            subIdx: subIdx,
        };
    }

    private async getAudioData(measurement: GenericMeasurement, mic: Point3D): Promise<AnnotatedTimeData> {
        let signal: Point2D[];
        try {
            signal = await this.audioTimeSignalProvider.getTimeSignal(measurement, mic, (progress) => {
                this.updateProgress(measurement, mic, "audio", progress);
            });
        } catch (error) {
            Toast.error(error?.message, "Please select a different measurement", this.uniqueId);
            return undefined;
        }

        const minmax = ArrayExtensions.minmax(signal.map((x) => x.y));
        const subIdx = MeasurementExtensions.micToSubIndexString(measurement, mic);
        return <AnnotatedTimeData>{
            measurement: measurement,
            dataPoints: signal,
            name: MeasurementExtensions.getDisplaySubIndexDisplayName(measurement, subIdx),
            minVal: minmax.min,
            maxVal: minmax.max,
            subIdx: subIdx,
        };
    }

    private updatePlotTimeSignals(): void {
        // Determine min and max values
        let minValue = Number.POSITIVE_INFINITY;
        let maxValue = Number.NEGATIVE_INFINITY;
        let maxDuration = 0;

        let result: PlotData[] = [];

        for (let j = 0; j < this.measurementChannelsPair.length; j++) {
            const timeData = this.measurementChannelsPair[j];

            maxDuration = Math.max(maxDuration, timeData.measurement.duration);

            const color = this.measurementColorService.getColor({
                measurementId: timeData.measurement.id,
            }).color.css;

            // Check if user has selected Moving Average representation
            if (!this.showRms) {
                maxValue = Math.max(maxValue, timeData.maxVal);
                minValue = Math.min(minValue, timeData.minVal);

                result.push(
                    new LineData(timeData.dataPoints, this.displayTimeUnit, this.signalUnit, null, {
                        name: timeData.name,
                        color: color,
                        opacity: 1.0,
                        displayValue: undefined,
                        displayValueColor: undefined,
                        id: timeData.subIdx,
                    })
                );
            } else {
                this.signalUnit = Pressure.pascalRms;

                result.push(
                    new LineData(
                        // Moving average range is currently a fixed number of 1001 as this shows a nice average line with still enough detail.
                        ArrayToMovingAverageArrayConverter.movingAverage(timeData.dataPoints, 1001),
                        this.displayTimeUnit,
                        this.signalUnitRMS,
                        null,
                        {
                            name: timeData.name,
                            color: color,
                            opacity: 1.0,
                            displayValue: undefined,
                            displayValueColor: undefined,
                            id: timeData.subIdx,
                        }
                    )
                );

                for (let index = 0; index < result.length; index++) {
                    for (let i = 0; i < result[index].data.length; i++) {
                        maxValue = Math.max(maxValue, result[index].data[i].y);
                        minValue = Math.min(minValue, result[index].data[i].y);
                    }
                }
            }
        }

        // Defined bounds of the graph
        if (result && result.length > 0) {
            const maxXAxisIsOutsideData = this.maxXAxis > maxDuration;
            const isZoomedIn = this.minXAxis !== 0 || this.maxXAxis !== this.maxTime;

            if (!isZoomedIn) {
                // If not zoomed in, adjust the x-axis to the full duration.
                this.minXAxis = 0;
                this.maxXAxis = maxDuration;
            } else if (maxXAxisIsOutsideData) {
                // Restrict the x-axis to the data if the original zoom is outside the data
                this.maxXAxis = maxDuration;

                // If the min axis of the zoom was outside the data,
                // reset the zoom because otherwise the user would look at an empty graph
                if (this.minXAxis >= maxDuration) {
                    this.minXAxis = 0;
                }
            }

            this.maxTime = maxDuration;

            if (this.zoomMode !== "y" && this.zoomMode !== "xy") {
                // Reset zoom
                this.minYAxis = minValue > 0 ? 0.9 * minValue : 1.1 * minValue;
                this.maxYAxis = maxValue > 0 ? 1.1 * maxValue : 0.9 * maxValue;
            }
        } else {
            result = [];
        }

        this.plotTimeSignals = result;
        this.updateLegendArray();
    }

    protected toAudioData(timeSet: TimeDataSetWithAudio, dataArrayIndex?: number): Point2D[] {
        let annotatedDatas: AnnotatedTimeData[];
        if (
            !timeSet.audioDatas ||
            timeSet.audioDatas.length === 0 ||
            (dataArrayIndex !== undefined && timeSet.audioDatas[dataArrayIndex] === undefined)
        ) {
            annotatedDatas = timeSet.timeDatas;
        } else {
            annotatedDatas = timeSet.audioDatas;
        }

        // Get the first existing index if no dataArrayIndex is provided
        if (!dataArrayIndex || annotatedDatas[dataArrayIndex] === undefined) {
            dataArrayIndex = annotatedDatas.findIndex((x) => x != undefined && x.dataPoints && x.dataPoints.length > 0);

            if (dataArrayIndex === -1) {
                // No data found
                return [];
            }
        }

        return annotatedDatas[dataArrayIndex].dataPoints;
    }

    /**
     * Update the legend.
     * Groups by measurement.
     * For each measurement, matches the order of the pairs
     */
    private updateLegendArray() {
        this.legendArray = this.currentTimeSignals.map((y): LegendGroup => {
            const result: LegendGroup = {
                title: y.measurement.name,
                entries: [],
            };

            const measurementChannelPair = this.measurementChannelsPair.filter(
                (x) => x.measurement.id == y.measurement.id
            );
            for (let i = 0; i < measurementChannelPair.length; i++) {
                const timeData = measurementChannelPair[i];
                if (timeData && timeData != undefined) {
                    result.entries.push({
                        name: timeData.name,
                        color: this.measurementColorService.getColor({
                            measurementId: timeData.measurement.id,
                        }).color.css,
                    });
                }
            }

            return result;
        });
    }

    //#endregion

    //#region Button callbacks

    public linkTimeRange(link: boolean): void {
        this.isTimeRangeLinked = link;
    }

    public linkSignalRange(link: boolean): void {
        this.isSignalRangeLinked = link;
    }

    public openTimeRangeModal(): void {
        void this.moduleInstance
            .createModal<TimeSelectionModalState, void>(WorkflowModalCatalog.TimeRangeModal)
            .withState({
                selectedTimeRange: this.timeSelectionRanges.length > 0 ? this.timeSelectionRanges[0] : undefined,
                currentSignals: this.currentTimeSignals,
            })
            .whenClosed(() => {
                this.timeRangeModal = undefined;
            })
            .show()
            .then((x) => {
                this.timeRangeModal = x;
            });
    }

    public openTimeSettingsModal(): void {
        if (this.timeSettingsModal && this.timeSettingsModal.isOpen) {
            this.timeSettingsModal.cancel();
            this.timeSettingsModal = undefined;
            return;
        }
        void this.moduleInstance
            .createModal<SettingsModalState<TimeModule>, void>(WorkflowModalCatalog.TimeSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.timeSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.timeSettingsModal = x;
            });
    }

    public exportVisual(): ImageExportState {
        const exportModalState = {
            measurementIds: this.selectedMeasurementIds,
            exportable: this.dataExportable,
            isLegendDisplayed: true,
            initialLegendState: this.showLegend,
            showLegend: (key: boolean) => {
                if (key !== undefined) {
                    this.showLegend = key;
                }
                return this.showLegend;
            },
            initialFileName:
                this.currentTimeSignals && this.currentTimeSignals.length > 0
                    ? this.currentTimeSignals[0].measurement.name + "_Time"
                    : undefined,
            initialLogoPosition: LogoPositions.bottomRight,
            namedProperties: [
                { name: "[Pressure]", value: this.showRms ? Pressure.pascalRms.unit : Pressure.pascal.unit },
            ],
        };

        return exportModalState;
    }

    public getSampleRate(measurement: GenericMeasurement): number {
        return measurement.parts.filter((x) => x.measurementType === MeasurementPartType.Audio)[0].device.sampleRate;
    }

    public timeSelectionRangesChanged(): void {
        if (this.timeRangeModal && this.timeRangeModal.isOpen) {
            this.timeRangeModal.updateState({
                selectedTimeRange: this.timeSelectionRanges.length > 0 ? this.timeSelectionRanges[0] : undefined,
                currentSignals: this.currentTimeSignals,
            });
        }
    }

    public openAddChannelModal(): void {
        // Gather all measurements from current time signals
        let measurements: GenericMeasurement[] = this.currentTimeSignals.map((timeSet) => timeSet.measurement);

        if (measurements.length <= 0) return;

        const timeDataSets = this.currentTimeSignals.filter(
            (timeSignal) => measurements.find((m) => m.id == timeSignal.measurement.id) !== undefined
        );

        const channelSelections = timeDataSets.map((x) => x.selectedChannels);

        // Merge channel selections across all measurements
        const mergedChannelSelection = channelSelections[0].map((_, index) =>
            channelSelections.some((selection) => selection[index])
        );

        measurements = MeasurementExtensions.getMeasurementsWithSameMicPositions(measurements[0], measurements);
        // Merge microphone positions for each time dataset
        const mergedMicrophonePositions = measurements
            .map((measurement) => MeasurementExtensions.getMicrophonePositionsForMeasurement(measurement))
            .reduce<Point3D[]>((acc, positions) => {
                // Fill in the position for the measurement
                positions.forEach((mic, index) => {
                    // Only set the position if it hasn�t been set yet in `acc`
                    if (!acc[index]) acc[index] = { x: mic.x, y: mic.y, z: mic.z };
                });
                return acc;
            }, []);

        // Prepare the state for the modal with merged selections and positions
        const state = <ManualChannelSelectionModalState>{
            microphonePositions: mergedMicrophonePositions,
            selectedSelection: mergedChannelSelection,
        };

        // Open the modal and handle the closure
        void this.moduleInstance
            .createModal<ManualChannelSelectionModalState, ManualChannelSelectionModalReturnState>(
                WorkflowModalCatalog.ManualChannelSelectionModal
            )
            .withState(state)
            .whenClosed((newState) => {
                // Gather selected channel indexes based on new state from modal
                const selectedChannelIndexes = newState.selectedSelection
                    .map((isSelected, index) => (isSelected ? index : -1))
                    .filter((index) => index !== -1);

                timeDataSets.forEach((timeDataSet) => {
                    const selectedMics: MeasurementChannelSelection = {
                        measurementId: timeDataSet.measurement.id,
                        selectedChannels: selectedChannelIndexes,
                    };

                    const idx = this.selectedChannelsForMeasurementIds.findIndex(
                        (x) => x.measurementId == timeDataSet.measurement.id
                    );

                    // Replace or add the updated measurement channel selection
                    if (idx >= 0) {
                        this.selectedChannelsForMeasurementIds.splice(idx, 1, selectedMics);
                    } else {
                        this.selectedChannelsForMeasurementIds.push(selectedMics);
                    }
                });

                // Notify of changes to the measurement selections
                this.selectedChannelsForMeasurementIdsChanged();
            })
            .show();
    }

    private updateProgress(measurement: GenericMeasurement, mic: Point3D, type: string, progress: number): void {
        const progressId = `${measurement.id}_${mic.x}_${mic.y}_${mic.z}_${type}`;
        this.progresses[progressId] = progress;

        this.progresses = { ...this.progresses };
    }

    private getChannelsToPlot(measurement: TimeDataSet): AnnotatedTimeData[] {
        return measurement.timeDatas.filter((x, i) => measurement.selectedChannels[i]);
    }

    //#endregion
}
